import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ACTIONS from "../../../../redux/action.js";
import i18n from "../../../../utils/i18n";
import ContactSelectionChips from "../contactPage/ContactSelectionChips.jsx";
import classes from "./aspContactPriceBand.module.scss";

const numberWithCommas = (x) => {
  if (x === null || x === undefined || x === "") return "";
  const n = typeof x === "number" ? x : parseInt(String(x).replace(/\D/g, ""), 10);
  if (Number.isNaN(n)) return "";
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const formatContBand = (min, max) => `${numberWithCommas(min)} — ${numberWithCommas(max)}`;

const getCustomOfferExplanation = (company) => {
  const isEtrade = company?.industry === "etrade";
  const outgoing = parseInt(company?.outgoinginvoices, 10);
  const incoming = parseInt(company?.incominginvoices, 10);
  const highInvoiceVolume =
    (!Number.isNaN(outgoing) && outgoing > 200) ||
    (!Number.isNaN(incoming) && incoming > 200);

  if (isEtrade && highInvoiceVolume) {
    return i18n.t("searchflow.etradecustomoffer");
  }

  return i18n.t("searchflow.customofferdefault");
};


/**
 * Fetches top ASP providers, shows expected monthly accounting fee band on contact step.
 * Independent of selectedProvGrp (ev / office / enterprise) tier cards.
 */
const AspContactPriceBand = ({
  fetchAspTopProvidersByMatchScore,
  match_score_weights,
  staticDisplay = false,
  variant = "contactPage",
}) => {
  const dispatch = useDispatch();
  const companyStore = useSelector((state) => state.company_store);
  const companyStoreRef = useRef(companyStore);
  companyStoreRef.current = companyStore;
  const [fetchState, setFetchState] = useState("idle");
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  const band = companyStore.asp_contact_price_band;
  const showBand = Boolean(companyStore.accounting);
  const selectedProvGrp = companyStore.selectedProvGrp;
  const singleDeduction = companyStore.discount?.singleDeduction;
  const proposedContPrice = companyStore.proposedContPrice;
  const originalContPrice = companyStore.originalContPrice;
  const aspContactContMin = companyStore.asp_contact_cont_min;
  const aspContactContMax = companyStore.asp_contact_cont_max;
  const providerCount = (companyStore.top_asp_providers_by_match_score || []).length;

  const customOfferExplanation = getCustomOfferExplanation(companyStore);

  const { usedDiscount, isDiscounted } = useMemo(() => {
    const d = companyStore.discount;
    if (!d) {
      return { usedDiscount: 0, isDiscounted: false };
    }
    let discountAmount = d.discount ?? 0;
    if (d.referral) {
      const small =
        companyStore.companytype === "singleprimary" ||
        companyStore.companytype === "singlesecondary" ||
        companyStore.companytype === "kiva" ||
        companyStore.companytype === "vszja";
      discountAmount = small ? d.discount : d.discountLarge;
    }
    let discounted = false;
    if (d.companyType === "any") discounted = true;
    if (
      typeof d.companyType === "string" &&
      d.companyType.includes(companyStore.companytype)
    ) {
      discounted = true;
    }
    return { usedDiscount: discountAmount || 0, isDiscounted: discounted };
  }, [companyStore.discount, companyStore.companytype]);

  useEffect(() => {
    if (staticDisplay || !showBand || typeof fetchAspTopProvidersByMatchScore !== "function") {
      return undefined;
    }
    let cancelled = false;
    setFetchState("loading");
    Promise.resolve(
      fetchAspTopProvidersByMatchScore(companyStoreRef.current, match_score_weights),
    )
      .then(() => {
        if (!cancelled) setFetchState("done");
      })
      .catch(() => {
        if (!cancelled) setFetchState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [showBand, staticDisplay, fetchAspTopProvidersByMatchScore, match_score_weights]);

  useEffect(() => {
    if (!showBand || !band) return;
    if (band.min == null || band.max == null) return;
    if (selectedProvGrp) return;

    let contMin = band.min;
    let contMax = band.max;
    const origContStr = formatContBand(contMin, contMax);

    if (isDiscounted && usedDiscount > 0 && singleDeduction) {
      contMin = Math.max(0, contMin - usedDiscount);
      contMax = Math.max(0, contMax - usedDiscount);
    }

    const contPriceStr = band.customOffer
      ? `${i18n.t("searchflow.customofferlabel")} (${formatContBand(contMin, contMax)})`
      : formatContBand(contMin, contMax);
    const fields = [
      { id: "proposedContPrice", data: contPriceStr, current: proposedContPrice },
      { id: "originalContPrice", data: origContStr, current: originalContPrice },
      { id: "asp_contact_cont_min", data: contMin, current: aspContactContMin },
      { id: "asp_contact_cont_max", data: contMax, current: aspContactContMax },
    ];
    fields.forEach(({ id, data, current }) => {
      if (current !== data) {
        dispatch(
          ACTIONS.addCompanyInfo({
            id,
            data,
            stateObject: "company_store",
          }),
        );
      }
    });
  }, [
    band,
    showBand,
    isDiscounted,
    usedDiscount,
    singleDeduction,
    proposedContPrice,
    originalContPrice,
    aspContactContMin,
    aspContactContMax,
    selectedProvGrp,
    dispatch,
  ]);

  if (!showBand) {
    return null;
  }

  if (!staticDisplay && fetchState === "loading") {
    return (
      <div className={classes.loadingWrap}>
        <CircularProgress size={28} sx={{ color: "#1c32f3" }} />
      </div>
    );
  }

  if (!band || (!staticDisplay && fetchState === "error")) {
    return null;
  }

  if (variant === "contactPage") {
    const toggleDetails = () => setIsDetailsExpanded((prev) => !prev);
    const min = band.min;
    const max = band.max;
    const hasAmount = min != null && max != null;
    const showDiscount =
      hasAmount && isDiscounted && usedDiscount > 0 && min > 0 && max > 0;
    const dMin = hasAmount ? Math.max(0, min - usedDiscount) : null;
    const dMax = hasAmount ? Math.max(0, max - usedDiscount) : null;
    const displayMin = showDiscount ? dMin : min;
    const displayMax = showDiscount ? dMax : max;
    const amountLabel = hasAmount
      ? `${formatContBand(displayMin, displayMax)} Ft`
      : null;

    return (
      <header className={classes.contactHeader}>
        <h1 className={classes.contactTitle}>
          {providerCount} {i18n.t("searchflow.providermatchsuffix")}
        </h1>
        <p className={classes.contactFeeLabel}>{i18n.t("searchflow.feelabel")}</p>
        <button
          type="button"
          className={classes.priceToggle}
          onClick={toggleDetails}
          aria-expanded={isDetailsExpanded}
        >
          <span className={classes.priceToggleLabel}>
            <span className={classes.contactPrice}>{amountLabel}</span>
          </span>
          <KeyboardArrowDownIcon
            className={`${classes.priceToggleIcon} ${
              isDetailsExpanded ? classes.priceToggleIconOpen : ""
            }`}
            aria-hidden="true"
          />
        </button>
        {showDiscount ? (
          <p className={classes.contactPriceStrike}>
            {formatContBand(min, max)} Ft
          </p>
        ) : null}
        <div
          className={`${classes.detailsPanel} ${
            isDetailsExpanded ? classes.detailsPanelOpen : ""
          }`}
          aria-hidden={!isDetailsExpanded}
        >
          <div className={classes.detailsPanelInner}>
            <p className={classes.detailsIntro}>{i18n.t("searchflow.detailsintro")}</p>
            <ContactSelectionChips className={classes.detailsChips} />
          </div>
        </div>
      </header>
    );
  }

  if (band.customOffer) {
    const hasAmount = band.min != null && band.max != null;
    return (
      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "action.hover",
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {i18n.t("searchflow.expectedfee")}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {i18n.t("searchflow.customofferlabel")}
        </Typography>
        {hasAmount ? (
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {formatContBand(band.min, band.max)} Ft
          </Typography>
        ) : null}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1, lineHeight: 1.4 }}
        >
          {customOfferExplanation}
        </Typography>
      </Box>
    );
  }

  const min = band.min;
  const max = band.max;
  const showDiscount =
    isDiscounted && usedDiscount > 0 && min > 0 && max > 0;
  const dMin = Math.max(0, min - usedDiscount);
  const dMax = Math.max(0, max - usedDiscount);

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "primary.main",
        bgcolor: "action.selected",
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {i18n.t("searchflow.expectedfeenet")}
      </Typography>
      {showDiscount ? (
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {formatContBand(dMin, dMax)} Ft
          </Typography>
          <Typography
            variant="body2"
            sx={{ textDecoration: "line-through", color: "text.secondary" }}
          >
            {formatContBand(min, max)} Ft
          </Typography>
        </Box>
      ) : (
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {formatContBand(min, max)} Ft
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {i18n.t("searchflow.estimatedrangenote")}
      </Typography>
    </Box>
  );
};

export default AspContactPriceBand;
